// admin/routes/RouteApprovalModal.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../common/Modal";
import RouteDetailMap from "../../map/RouteDetailMap";
import Button from "../../common/Button";
import { approveRoute, rejectRoute } from "@/store/slices/adminSlice";

const RouteApprovalModal = ({ route, onClose }) => {
  const dispatch = useDispatch();
  const { isUpdating, updateError } = useSelector((state) => state.admin);
  
  const [rejectReason, setRejectReason] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleApprove = async () => {
    try {
      await dispatch(approveRoute(route._id)).unwrap();
      onClose();
    } catch (error) {
      // Error is handled by Redux state
      console.error("Approval failed:", error);
    }
  };

  const handleReject = async () => {
    // Validate rejection reason
    if (!rejectReason.trim()) {
      setValidationError("Rejection reason is required");
      return;
    }
    
    if (rejectReason.trim().length < 10) {
      setValidationError("Rejection reason must be at least 10 characters");
      return;
    }

    setValidationError("");

    try {
      await dispatch(rejectRoute({ 
        routeId: route._id, 
        rejection_reason: rejectReason.trim() 
      })).unwrap();
      onClose();
    } catch (error) {
      // Error is handled by Redux state
      console.error("Rejection failed:", error);
    }
  };

  return (
    <Modal open={true} onClose={onClose} title="" showCloseButton={false}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            {route.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Submitted by {route.creator_id?.name || route.creator?.name || "Unknown"} •{" "}
            {new Date(route.created_at).toLocaleDateString()}
          </p>
        </div>

        {/* Map View */}
        <div className="h-64 sm:h-80 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <RouteDetailMap 
            route={{
              ...route,
              // Extract coordinates from GeoJSON if present
              coordinates: route.coordinates?.coordinates || route.coordinates || []
            }} 
          />
        </div>

        {/* Route Metadata */}
        <div className="grid grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Distance</div>
            <div className="font-semibold text-gray-800 dark:text-gray-100">
              {route.distance} km
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Difficulty</div>
            <div className="font-semibold text-gray-800 dark:text-gray-100">
              {route.difficulty || "N/A"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">State</div>
            <div className="font-semibold text-gray-800 dark:text-gray-100">
              {route.state_code || route.state || "N/A"}
            </div>
          </div>
        </div>

        {/* Rejection Reason Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rejection Reason (if rejecting)
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              setValidationError("");
            }}
            placeholder="Provide a reason for rejection (minimum 10 characters)..."
            className="w-full h-24 p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          {validationError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
              {validationError}
            </p>
          )}
        </div>

        {/* Error Display */}
        {updateError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{updateError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={isUpdating}
          >
            {isUpdating ? "Rejecting..." : "Reject"}
          </Button>
          <Button
            variant="primary"
            onClick={handleApprove}
            disabled={isUpdating}
          >
            {isUpdating ? "Approving..." : "Approve"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RouteApprovalModal;
